import React, { useMemo } from 'react'
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Styles } from '../../../../types'
import { getCemFeatures } from '../../../../store/userInfo/userInfoSlice'
import { isCemFeatureEnabled } from '../../../../rsmCoreComponents/utils/featureFlagUtils'
import { CEM_FEATURE_PAYMENT_CREATE_INSTALLMENTS } from '../../../../utils/constants/constants'

const styles: Styles = {
  PaymentTypeSelection: (theme) => ({
    paddingTop: '3rem',
    [theme.breakpoints.only('mobile')]: {
      paddingTop: '1rem',
    },
  }),
  CategoryHeader: () => ({
    fontFamily: 'Prelo-Bold, sans-serif',
    fontSize: '1.25rem',
    lineHeight: '1.5rem',
    paddingBottom: '1.25rem',
  }),
  CategoryLegend: (theme) => ({
    fontFamily: 'Prelo-Black, sans-serif',
    fontSize: '1rem',
    paddingBottom: '0.5rem',
    color: theme.palette.text.primary,
    [theme.breakpoints.only('mobile')]: {
      paddingBottom: 0,
    },
  }),
  RadioButton: (theme) => ({
    [theme.breakpoints.only('mobile')]: {
      paddingTop: '0.3125rem',
      paddingBottom: '0.3125rem',
    },
  }),
}

interface PaymentCategoryProps {
  paymentCategoryChanged: (category: string) => void
  isScheduledPaymentDisabled?: boolean
}

const PaymentCategory = ({
  paymentCategoryChanged,
  isScheduledPaymentDisabled,
}: PaymentCategoryProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext()
  const cemFeatures = useSelector(getCemFeatures)
  const isAccountManager = useMemo(
    () =>
      isCemFeatureEnabled(
        CEM_FEATURE_PAYMENT_CREATE_INSTALLMENTS,
        cemFeatures,
        'any',
      ),
    [cemFeatures],
  )

  const paymentCategories = [
    t('Invoicing.PayNow'),
    t('Invoicing.SchedulePayment'),
    ...(isAccountManager ? [t('Invoicing.SetupInstallments')] : []),
  ]

  return (
    <Box sx={styles.PaymentTypeSelection}>
      <FormControl component="fieldset">
        <Box component="legend">
          <Box
            component="h2"
            sx={styles.CategoryHeader}
            data-testid="Lbl_Invoicing_PaymentInformationHeader">
            {t('Invoicing.PaymentInformationHeader')}
          </Box>
          <Box
            id="RadioPaymentHelperText"
            data-testid="Txt_Invoicing_HelperText_PaymentInfo"
            sx={{
              marginBottom: '1rem',
              color: isScheduledPaymentDisabled
                ? 'rsmGray.disabled'
                : 'inherit',
              fontWeight: isScheduledPaymentDisabled ? 500 : 'normal',
            }}>
            {isScheduledPaymentDisabled ? (
              <Box>
                <strong>{t('Invoicing.PaymentMethod')}</strong>
                <br />
                <strong>
                  <i>Note:</i>
                </strong>{' '}
                <Trans
                  i18nKey="Invoicing.ScheduledPlanNote"
                  components={{ b: <b />, i: <i /> }}
                />
              </Box>
            ) : (
              <strong>{t('Invoicing.PaymentMethod')}</strong>
            )}
          </Box>
        </Box>
        <Controller
          name="paymentCategory"
          control={control}
          defaultValue={paymentCategories[0]}
          render={({ field: { onChange, value, ref } }) => (
            <RadioGroup
              aria-describedby="RadioPaymentHelperText"
              ref={ref}
              row
              value={value}
              onChange={(e) => {
                onChange(e)
                paymentCategoryChanged(e.target.value)
              }}
              defaultValue={paymentCategories[0]}>
              {paymentCategories.map((option: string) => (
                <FormControlLabel
                  key={option}
                  sx={{
                    marginRight: '3.5rem',
                  }}
                  value={option}
                  label={option}
                  control={<Radio sx={styles.RadioButton} />}
                  disabled={
                    isScheduledPaymentDisabled &&
                    option !== t('Invoicing.PayNow')
                  }
                  data-test-id={`Radio_Payment_${option.replace(/\s+/g, '')}`}
                />
              ))}
            </RadioGroup>
          )}
        />
      </FormControl>
    </Box>
  )
}

export default PaymentCategory
